import { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import debug from "@plannel/utils/debug-util";
import { isEmpty, cloneDeep, isEqual, includes, keys } from "lodash";
import { useDispatch } from "react-redux";
import { Box, Chip, Divider, FormControl, FormGroup, FormLabel, InputLabel, ListItem, MenuItem, Select } from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import codeUtil from "@plannel/pages/utils/code-util";
import reduxUtil from "@plannel/utils/redux-util";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import { grey } from "@mui/material/colors";
import { AddButton, CalculationButton } from "@plannel/components/ActionIconButton";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";
import ComputeMeasureModal from "../dp-workbench/dp-workbench-modal/ComputeMeasureModal";
import WorkbenchUtil from "../dp-workbench/WorkbenchUtil";
import colorUtil from "@plannel/pages/utils/color-util";

const ChipsApplied = props => {
  const { formulaList, setFormulaList, t, hasLevelPlans, customer, item } = props;

  const getInitialLabel = row => {
    if (!isEmpty(row)) {
      const label = `${row?.custHrchy?.name ?? customer.name}, ${row?.itemHrchy?.name ?? item.name}: ${t(row?.initialValue)}`;
      if (includes(hasLevelPlans, row?.initialValue)) {
        return `${label}(${row?.planCustLevel?.name}, ${row?.planItemLevel?.name})`;
      }
      return label;
    }
    return "";
  };

  const handleDelete = chipToDelete => () => {
    setFormulaList(chips => chips.filter(chip => chip !== chipToDelete));
  };

  return (
    <Box sx={{ mt: 1.5, p: 0.5, pt: 1, textAlign: "center", gap: 1, minHeight: 200, height: "calc(100% - 400px)", overflow: "auto", borderRadius: 1, backgroundColor: grey[100] }}>
      {formulaList?.map(data => {
        return (
          <ListItem
            key={`${data?.initialValue}${data?.itemHrchy?.id ?? item.id}${data?.custHrchy?.id ?? customer.id}`}
            sx={{ p: 0.5 }}>
            <Chip
              sx={{ size: "small" }}
              label={getInitialLabel(data)}
              onDelete={handleDelete(data)}
              variant="outlined"
            />
          </ListItem>
        );
      })}
    </Box>
  );
};

const BoxAddBtn = props => {
  const { handleAddInitial } = props;
  return (
    <Box sx={{ display: "inline-flex", width: "9%", marginLeft: "auto", height: "100%", justifyContent: "center", alignItems: "center" }}>
      <AddButton
        sx={{ textAlign: "right" }}
        onClick={handleAddInitial}
      />
    </Box>
  );
};

const FormControlLevels = props => {
  const { t, itemLevels, custLevels, custLevelNm, itemLevelNm, selectedCustLv, selectedItemLv, initialValue, hasLevelPlans, handleChangeSelect, handleAddInitial, dpSettingCode } = props;
  const { CUST_LEVEL, ITEM_LEVEL } = dpSettingCode;

  return includes(hasLevelPlans, initialValue) ? (
    <Box sx={{ mt: 1, mb: 1, height: 50 }}>
      <FormControl sx={{ width: "45%", mr: "1%" }}>
        <InputLabel>{t(CUST_LEVEL)}</InputLabel>
        <Select
          id={CUST_LEVEL}
          name={custLevelNm}
          value={selectedCustLv?.id ?? ""}
          label={t(CUST_LEVEL)}
          size="small"
          variant="outlined"
          onChange={event => {
            handleChangeSelect(event);
          }}>
          {custLevels?.map(custLevel => {
            return (
              <MenuItem
                key={custLevel.id}
                value={custLevel.id}>
                {custLevel.name}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <FormControl sx={{ width: "45%" }}>
        <InputLabel>{t(ITEM_LEVEL)}</InputLabel>
        <Select
          id={ITEM_LEVEL}
          name={itemLevelNm}
          value={selectedItemLv?.id ?? ""}
          label={t(ITEM_LEVEL)}
          size="small"
          variant="outlined"
          onChange={event => {
            handleChangeSelect(event);
          }}>
          {itemLevels?.map(itemLevel => {
            return (
              <MenuItem
                key={itemLevel.id}
                value={itemLevel.id}>
                {itemLevel.name}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {handleAddInitial ? <BoxAddBtn handleAddInitial={handleAddInitial} /> : null}
    </Box>
  ) : null;
};

const BoxSelectInitialValues = ({ t, setDialogInfo, itemLevelCds: itemLevels, versionList, custLevelCds: custLevels, appliedList, setAppliedList, hasLevelPlans, dpSettingCode, oriMeasureList, initialValues, customerHrchies, itemHrchies, customer, item, filteredInitialVals, posActualFlg = false }) => {
  const { CUST_LEVEL, ITEM_LEVEL } = dpSettingCode;
  const { NOTINITIAL, LAST_RELEASED_VERSION, DP_VERSION, BASELINE_FORECAST } = codeUtil.dpInitalValues;

  const [selectedCustLevel, setSelectedCustLevel] = useState(custLevels?.[0]);
  const [selectedItemLevel, setSelectedItemLevel] = useState(itemLevels?.[0]);

  const filteredCustHrchys = customerHrchies?.filter(hrchy => isEqual(hrchy.hrchyLvId, selectedCustLevel.id));
  const filteredItemHrchys = itemHrchies?.filter(hrchy => isEqual(hrchy.hrchyLvId, selectedItemLevel.id));
  const [selectedCustHrchy, setSelectedCustHrchy] = useState(customerHrchies[0]);
  const [selectedItemHrchy, setSelectedItemHrchy] = useState(itemHrchies[0]);
  const [initialValue, setInitialValue] = useState(initialValues?.[0]);

  const filteredCustLevels = custLevels?.filter(lv => lv.seqNum >= selectedCustLevel.seqNum);
  const filteredItemLevels = itemLevels?.filter(lv => lv.seqNum >= selectedItemLevel.seqNum);
  const [planCustLevel, setPlanCustLevel] = useState(custLevels[0]);
  const [planItemLevel, setPlanItemLevel] = useState(itemLevels[0]);

  // Last relased version or DP version일 때 사용
  const [selectVersionId, setSelectVersionId] = useState("");
  const [activeVersionList, setActiveVersionList] = useState([]);

  const defaultComputeList = WorkbenchUtil.getDefaultComputeList(initialValue);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [computeMeasureList, setComputeMeasureList] = useState(defaultComputeList);
  const [initialMeasureList, setInitialMeasureList] = useState(codeUtil.getSourceMeasureList(oriMeasureList, initialValue, posActualFlg));
  const initialTarget = oriMeasureList?.filter(item => item?.measureCd?.includes("SF") || item.measureCd.includes("MF"));

  const validationAddInitial = addVal => {
    if (!isEmpty(appliedList)) {
      for (const val of appliedList) {
        if (isEqual(addVal?.itemHrchy?.id, val?.itemHrchy?.id) && isEqual(addVal?.custHrchy?.id, val.custHrchy?.id)) {
          setDialogInfo({ open: true, title: "warning", content: t("MSG_SelectDiffrHrchy"), el: { type: "HRCHY", addVal } });
          return false;
        }
      }
    }
    return true;
  };

  const handleAddInitial = () => {
    const addInitialValue = {
      custLevel: selectedCustLevel,
      itemLevel: selectedItemLevel,
      custHrchy: selectedCustHrchy,
      itemHrchy: selectedItemHrchy,
      initialValue,
      planItemLevel,
      planCustLevel,
      formula: computeMeasureList,
    };

    const originalList = cloneDeep(appliedList);

    if (isEqual(initialValue, LAST_RELEASED_VERSION) || isEqual(initialValue, DP_VERSION)) {
      addInitialValue.copyTarget = selectVersionId;
    }

    if (!isEmpty(appliedList) && isEmpty(originalList.filter(val => isEqual(val.custLevel.id, selectedCustLevel.id) && isEqual(val.itemLevel.id, selectedItemLevel.id)))) {
      setDialogInfo({ open: true, title: "warning", content: t("MSG_ConfirmSameLevel"), el: { type: "CONFIG" } });
    } else {
      if (validationAddInitial(addInitialValue)) {
        setAppliedList([...originalList, addInitialValue]);
      }
    }
  };

  const filterActionItem = params => {
    if (params.type === "click") {
      setFilterModalOpen(true);
    }
  };

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };

  const handleChangeSelect = event => {
    const targetNm = event?.target?.name;
    const value = event?.target?.value;

    if (isEqual(targetNm, CUST_LEVEL)) {
      const selectLevel = custLevels.filter(lv => isEqual(lv.id, value));
      setSelectedCustLevel(selectLevel[0]);

      if (value === customer.id) {
        setSelectedCustHrchy(null);
      }
    } else if (isEqual(targetNm, ITEM_LEVEL)) {
      const selectLevel = itemLevels.filter(lv => isEqual(lv.id, value));
      setSelectedItemLevel(selectLevel[0]);

      if (value === item.id) {
        setSelectedItemHrchy(null);
      }
    } else if (isEqual(targetNm, "customerHrchy")) {
      const hrchy = customerHrchies?.filter(hrchy => isEqual(hrchy.id, value));
      setSelectedCustHrchy(hrchy[0]);
      const sameList = cloneDeep(appliedList).filter(val => isEqual(val?.custHrchy?.id, value) && isEqual(val?.itemHrchy?.id, selectedItemHrchy?.id));

      if (!isEmpty(sameList)) {
        const sameValue = sameList[0];
        setComputeMeasureList(sameValue?.formula);
        setInitialValue(sameValue?.initialValue);

        if (includes(hasLevelPlans, initialValue)) {
          setPlanCustLevel(sameValue?.planCustLevel);
          setPlanItemLevel(sameValue?.planItemLevel);
        }
      }
    } else if (isEqual(targetNm, "itemHrchy")) {
      const hrchy = itemHrchies?.filter(hrchy => isEqual(hrchy.id, value));
      setSelectedItemHrchy(hrchy[0]);
      const sameList = cloneDeep(appliedList).filter(val => isEqual(val?.itemHrchy?.id, value) && isEqual(val?.custHrchy?.id, selectedCustHrchy?.id));

      if (!isEmpty(sameList)) {
        const sameValue = sameList[0];
        setComputeMeasureList(sameValue?.formula);
        setInitialValue(sameValue?.initialValue);

        if (includes(hasLevelPlans, initialValue)) {
          setPlanCustLevel(sameValue?.planCustLevel);
          setPlanItemLevel(sameValue?.planItemLevel);
        }
      }
    } else if (isEqual(targetNm, "initialValue")) {
      setInitialValue(value);
      if (!isEmpty(versionList)) {
        let filterData = [];
        if (value === LAST_RELEASED_VERSION) {
          filterData = versionList?.filter(x => x.statusCd === "RELEASED");
        } else if (value === DP_VERSION) {
          filterData = versionList?.filter(x => x.statusCd === "SCENARIO");
        } else if (value === BASELINE_FORECAST) {
          filterData = versionList?.filter(x => x.statusCd === "SCENARIO" && x.parent === null);
        }
        if (!isEmpty(filterData)) {
          setActiveVersionList(filterData);
          setSelectVersionId(filterData[0].id);
        } else {
          if (value !== LAST_RELEASED_VERSION) {
            setActiveVersionList(versionList);
            setSelectVersionId(versionList[0].id);
          } else {
            setActiveVersionList([]);
            setSelectVersionId("");
          }
        }
      }
    } else if (isEqual(targetNm, "planCustLevel")) {
      const selectLevel = custLevels.filter(lv => isEqual(lv.id, value));
      setPlanCustLevel(selectLevel[0]);
    } else if (isEqual(targetNm, "planItemLevel")) {
      const selectLevel = itemLevels.filter(lv => isEqual(lv.id, value));
      setPlanItemLevel(selectLevel[0]);
    }
  };

  useEffect(() => {
    if (!isEmpty(selectedCustLevel) && !isEmpty(customerHrchies) && !isEmpty(custLevels)) {
      const custHrchys = customerHrchies?.filter(hrchy => isEqual(hrchy.hrchyLvId, selectedCustLevel.id));
      if (!isEmpty(custHrchys)) {
        setSelectedCustHrchy(custHrchys[0]);
      }

      const planLevels = custLevels?.filter(lv => lv.seqNum >= selectedCustLevel.seqNum);
      if (!isEmpty(planLevels)) {
        setPlanCustLevel(planLevels[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustLevel]);

  useEffect(() => {
    if (!isEmpty(selectedItemLevel) && !isEmpty(itemHrchies) && !isEmpty(custLevels)) {
      const itemHrchys = itemHrchies?.filter(hrchy => isEqual(hrchy.hrchyLvId, selectedItemLevel.id));
      if (!isEmpty(itemHrchys)) {
        setSelectedItemHrchy(itemHrchys[0]);
      }

      const planLevels = itemLevels?.filter(lv => lv.seqNum >= selectedItemLevel.seqNum);
      if (!isEmpty(planLevels)) {
        setPlanItemLevel(planLevels[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemLevel]);

  const getSelectedMeasureList = selectedInitialVal => {
    if (!isEmpty(selectedCustHrchy) && !isEmpty(selectedItemHrchy)) {
      for (const addRow of appliedList) {
        if (isEqual(selectedCustHrchy?.id, addRow?.custHrchy?.id) && isEqual(selectedItemHrchy?.id, addRow?.itemHrchy?.id) && isEqual(initialValue, addRow?.initialValue)) {
          return appliedList.formula;
        }
      }
    }
    return WorkbenchUtil.getDefaultComputeList(selectedInitialVal);
  };

  useEffect(() => {
    if (!isEmpty(oriMeasureList) && initialValue) {
      const sourceList = codeUtil.getSourceMeasureList(oriMeasureList, initialValue, posActualFlg);
      if (sourceList) {
        setInitialMeasureList(sourceList);
      }
      setComputeMeasureList(getSelectedMeasureList(initialValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, oriMeasureList, posActualFlg]);

  useEffect(() => {
    if (!isEmpty(versionList)) {
      let filterData = [];
      if (initialValue === LAST_RELEASED_VERSION) {
        filterData = versionList?.filter(x => x.statusCd === "RELEASED");
      } else if (initialValue === DP_VERSION) {
        filterData = versionList?.filter(x => x.statusCd === "SCENARIO");
      } else if (initialValue === BASELINE_FORECAST) {
        filterData = versionList?.filter(x => x.statusCd === "SCENARIO" && x.parent === null);
      }
      if (!isEmpty(filterData)) {
        setActiveVersionList(filterData);
        setSelectVersionId(filterData[0].id);
      } else {
        if (initialValue !== LAST_RELEASED_VERSION) {
          setActiveVersionList(versionList);
          setSelectVersionId(versionList[0].id);
        } else {
          setActiveVersionList([]);
          setSelectVersionId("");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionList]);

  return (
    <>
      <FormLabel
        key={"hrchyLv"}
        component="legend"
        sx={{ px: 1, mt: 1, mb: 0.5 }}>
        {t("hrchyLv")}
      </FormLabel>
      <FormGroup sx={{ width: "100%", minWidth: 100 }}>
        <Box sx={{ mt: 1 }}>
          <FormControl sx={{ width: "45%", mr: "1%" }}>
            <InputLabel>{t(CUST_LEVEL)}</InputLabel>
            <Select
              id={CUST_LEVEL}
              name={CUST_LEVEL}
              value={selectedCustLevel?.id ?? ""}
              label={t(CUST_LEVEL)}
              size="small"
              variant="outlined"
              onChange={event => {
                handleChangeSelect(event);
              }}>
              {custLevels?.map(custLevel => {
                return (
                  <MenuItem
                    key={custLevel.id}
                    value={custLevel.id}>
                    {custLevel.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {!isEqual(selectedCustLevel.id, customer.id) ? (
            <FormControl sx={{ width: "45%", mr: "1%" }}>
              <InputLabel>{t("customerHrchy")}</InputLabel>
              <Select
                id={"custHrchy"}
                name={"customerHrchy"}
                value={selectedCustHrchy ? selectedCustHrchy.id : ""}
                label={t("customerHrchy")}
                size="small"
                variant="outlined"
                onChange={event => {
                  handleChangeSelect(event);
                }}>
                {filteredCustHrchys?.map(hrchy => {
                  return (
                    <MenuItem
                      key={hrchy.id}
                      value={hrchy.id}>
                      {hrchy.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          ) : null}
        </Box>
        <Box sx={{ mt: 1 }}>
          <FormControl sx={{ width: "45%", mr: "1%" }}>
            <InputLabel>{t(ITEM_LEVEL)}</InputLabel>
            <Select
              id={ITEM_LEVEL}
              name={ITEM_LEVEL}
              value={selectedItemLevel?.id ?? ""}
              label={t(ITEM_LEVEL)}
              size="small"
              variant="outlined"
              onChange={event => {
                handleChangeSelect(event);
              }}>
              {itemLevels?.map(itemLevel => {
                return (
                  <MenuItem
                    key={itemLevel.id}
                    value={itemLevel.id}>
                    {itemLevel.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {!isEqual(selectedItemLevel.id, item.id) ? (
            <FormControl sx={{ width: "45%", mr: "1%" }}>
              <InputLabel>{t("itemHrchy")}</InputLabel>
              <Select
                id={"itemHrchy"}
                name={"itemHrchy"}
                value={selectedItemHrchy ? selectedItemHrchy.id : ""}
                label={t("itemHrchy")}
                size="small"
                variant="outlined"
                onChange={event => {
                  handleChangeSelect(event);
                }}>
                {filteredItemHrchys?.map(hrchy => {
                  return (
                    <MenuItem
                      key={hrchy.id}
                      value={hrchy.id}>
                      {hrchy.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          ) : null}
        </Box>
      </FormGroup>
      <FormLabel
        key={"Initial Level"}
        component="legend"
        sx={{ px: 1, mt: 1, mb: 0.5 }}>
        {t("initialValue")}
      </FormLabel>
      <FormGroup sx={{ width: "100%", minWidth: 100 }}>
        <Box sx={{ mt: 1 }}>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel>{t("demandPlan")}</InputLabel>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Select
                sx={{ width: "70%", mr: "1%" }}
                id="initialValue"
                name="initialValue"
                value={initialValue}
                label={t("demandPlan")}
                size="small"
                variant="outlined"
                onChange={event => {
                  handleChangeSelect(event);
                }}>
                {filteredInitialVals?.map(initalValue => {
                  return (
                    <MenuItem
                      key={initalValue}
                      value={initalValue}>
                      {t(initalValue)}
                    </MenuItem>
                  );
                })}
              </Select>
              <CalculationButton
                sx={{ ml: 1 }}
                label="computeExpressSetting"
                color={!isEmpty(computeMeasureList) ? "primary" : "black"}
                disabled={initialValue === NOTINITIAL}
                id={"initialFormula"}
                onClick={filterActionItem}
              />
              {!includes(hasLevelPlans, initialValue) && !(initialValue === DP_VERSION) ? <BoxAddBtn handleAddInitial={handleAddInitial} /> : null}
            </Box>
            <Box sx={{ mt: 1, display: initialValue === DP_VERSION ? "flex" : "none", alignItems: "center" }}>
              <FormControl sx={{ width: "70%" }}>
                <InputLabel>{t("version")}</InputLabel>
                <Select
                  name="versionId"
                  label={t("version")}
                  value={selectVersionId}
                  disabled={initialValue !== DP_VERSION}
                  size="small"
                  variant="outlined"
                  onChange={event => {
                    setSelectVersionId(event.target.value);
                  }}>
                  {activeVersionList?.map(version => {
                    return (
                      <MenuItem
                        value={version.id}
                        key={version.id}>
                        {version.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <BoxAddBtn handleAddInitial={handleAddInitial} />
            </Box>
            <FormControlLevels
              t={t}
              initialValue={initialValue}
              custLevels={filteredCustLevels}
              itemLevels={filteredItemLevels}
              hasLevelPlans={hasLevelPlans}
              ITEM_LEVEL={ITEM_LEVEL}
              CUST_LEVEL={CUST_LEVEL}
              handleChangeSelect={handleChangeSelect}
              custLevelNm={"planCustLevel"}
              itemLevelNm={"planItemLevel"}
              selectedCustLv={planCustLevel}
              selectedItemLv={planItemLevel}
              handleAddInitial={handleAddInitial}
              dpSettingCode={dpSettingCode}
              versionList={versionList}
            />
          </FormControl>
        </Box>
      </FormGroup>
      <ChipsApplied
        t={t}
        formulaList={appliedList}
        setFormulaList={setAppliedList}
        hasLevelPlans={hasLevelPlans}
        customer={customer}
        item={item}
      />
      <ComputeMeasureModal
        open={filterModalOpen}
        close={handelFilterClose}
        modalResultData={setComputeMeasureList}
        target={initialTarget?.length > 0 ? initialTarget : oriMeasureList}
        source={initialMeasureList}
        computeList={computeMeasureList}
      />
    </>
  );
};

const DpInitialSettingModal = props => {
  // posActualFlg — BF Source Data(POS 실적) 옵션. Compute Setting 소스 후보에 POS 계열 measure 와
  // SUGGESTED_MP_QTY 를 넣을지 결정한다. 호출측(DP Settings / 버전·시나리오 모달)이 자기 상태를 넘겨준다.
  const { t, close, open, itemLevelCds, custLevelCds, initialValues, oriMeasureList, configDatas, modalResultData, versionList, setVersionId, posActualFlg = false } = props;
  const {
    customer,
    item,
    dpConfig: { INITIAL_DEMAND },
    dpInitalValues,
    module: { DP },
    dpSettingCode,
  } = codeUtil;

  const { snackBarMessageColor } = colorUtil;

  const { NOTINITIAL, CUSTOMIZATION, LAST_RELEASED_VERSION, DP_VERSION } = dpInitalValues;
  const filteredInitialVals = initialValues.filter(val => !isEqual(CUSTOMIZATION, val));
  const { BASELINE_FORECAST, ITEM_LEVEL_ID, CUST_LEVEL_ID, ROLLING_RANGE } = dpSettingCode;
  const hasLevelPlans = [BASELINE_FORECAST];
  const initialTarget = oriMeasureList?.filter(item => item?.measureCd?.includes("SF") || item.measureCd.includes("MF"));

  const reduxViewState = cloneDeep(reduxUtil.getViewState("dpInitialSetting"));
  const reduxDispatch = useDispatch();

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const [itemHrchies, setItemHrchies] = useState(reduxViewState?.itemHrchies ?? []);
  const [customerHrchies, setCustomerHrchies] = useState(reduxViewState?.customerHrchies ?? []);

  const [defaultValue, setDefaultValue] = useState(initialValues?.[0]);
  const [defaultPlanCustLevel, setDefaultPlanCustLevel] = useState(custLevelCds?.[0]);
  const [defaultPlanItemLevel, setDefaultPlanItemLevel] = useState(itemLevelCds?.[0]);

  // Last relased version or DP version일 때 사용
  const [selectVersionId, setSelectVersionId] = useState("");
  const [activeVersionList, setActiveVersionList] = useState([]);

  const [saveList, setSaveList] = useState([]);

  const defaultComputeList = WorkbenchUtil.getDefaultComputeList(defaultValue);
  const [computeMeasureList, setComputeMeasureList] = useState(defaultComputeList);
  const [initialMeasureList, setInitialMeasureList] = useState(oriMeasureList);

  const setIntialConfig = saveList => {
    const saveItems = [];

    for (const save of saveList) {
      const initialObj = {
        itemHrchyId: save?.itemHrchy?.id,
        custHrchyId: save?.custHrchy?.id,
        itemLvId: save?.itemLevel?.id,
        custLvId: save?.custLevel?.id,
        initialPlan: save?.initialValue,
        formula: save?.formula,
      };

      if (includes(hasLevelPlans, initialObj.initialPlan)) {
        initialObj.planCustLvId = save.planCustLevel.id;
        initialObj.planItemLvId = save.planItemLevel.id;
      }

      if (isEqual(initialObj.initialPlan, LAST_RELEASED_VERSION) || isEqual(initialObj.initialPlan, DP_VERSION)) {
        initialObj.copyTarget = save.copyTaget;
      }

      saveItems.push(initialObj);
    }

    const data = { defaultValue };
    data[defaultValue] = computeMeasureList?.length > 0 ? computeMeasureList : [];
    data.defaultValue = defaultValue;

    if (isEqual(defaultValue, LAST_RELEASED_VERSION) || isEqual(defaultValue, DP_VERSION)) {
      data.copyTarget = selectVersionId;
    }

    if (includes(hasLevelPlans, defaultValue)) {
      data[CUST_LEVEL_ID] = defaultPlanCustLevel?.id ?? custLevelCds[0].id;
      data[ITEM_LEVEL_ID] = defaultPlanItemLevel?.id ?? itemLevelCds[0].id;
    }
    return { ...data, [CUSTOMIZATION]: saveItems };
  };

  const handleChangeSelect = event => {
    const targetNm = event?.target?.name;
    const value = event?.target?.value;

    if (isEqual(targetNm, "defaultValue")) {
      setDefaultValue(value);
      if (!isEmpty(versionList)) {
        let filterData = [];
        if (event.target.value === LAST_RELEASED_VERSION) {
          filterData = versionList?.filter(x => x.statusCd === "RELEASED");
        } else if (event.target.value === DP_VERSION) {
          filterData = versionList?.filter(x => x.statusCd === "SCENARIO");
        } else if (event.target.value === BASELINE_FORECAST) {
          filterData = versionList?.filter(x => x.statusCd === "SCENARIO" && x.parent === null);
        }
        if (!isEmpty(filterData)) {
          setActiveVersionList(filterData);
          setSelectVersionId(filterData[0].id);
        } else {
          if (event.target.value !== LAST_RELEASED_VERSION) {
            setActiveVersionList(versionList);
            setSelectVersionId(versionList[0].id);
          } else {
            setActiveVersionList([]);
            setSelectVersionId("");
          }
        }
      }
    } else if (isEqual(targetNm, "defaultPlanCustLevel")) {
      const selectLevel = custLevelCds.filter(lv => isEqual(lv.id, value));
      setDefaultPlanCustLevel(selectLevel[0]);
    } else if (isEqual(targetNm, "defaultPlanItemLevel")) {
      const selectLevel = itemLevelCds.filter(lv => isEqual(lv.id, value));
      setDefaultPlanItemLevel(selectLevel[0]);
    }
  };

  const handleClose = () => {
    const originData = configDatas?.objVal?.[CUSTOMIZATION] ?? [];
    if (!isEqual(originData, saveList)) {
      setDialogInfo({ open: true, title: "cancel", content: t("MSG_ChangeConfirm") });
    } else {
      setSaveList([]);
      close();
    }
  };

  const filterActionItem = params => {
    if (params.type === "click") {
      setFilterModalOpen(true);
    }
  };

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };

  const handleDialog = (action, _falseAction, el) => {
    if (action === "CANCEL") {
      close();
    } else if (action === "WARNING") {
      if (isEqual(el.type, "HRCHY")) {
        const { addVal } = el;
        const valueList = cloneDeep(saveList).filter(val => !isEqual(val.itemHrchy.id, addVal.itemHrchy.id) && !isEqual(val.custHrchy.id, addVal.custHrchy.id));
        setSaveList([...valueList, addVal]);
      }
    }
  };

  const handleApply = () => {
    const saveItems = setIntialConfig(saveList);

    if (isEmpty(configDatas)) {
      const row = {
        moduleCd: DP,
        activeFlg: true,
        configCd: INITIAL_DEMAND,
        boolVal: false,
        strVal: defaultValue,
        objVal: saveItems,
      };
      modalResultData({ ...row });
    } else {
      const rollingRange = configDatas[0]?.objVal?.[ROLLING_RANGE];
      if (rollingRange) {
        saveItems[ROLLING_RANGE] = rollingRange;
      }

      modalResultData({ ...configDatas, strVal: CUSTOMIZATION, objVal: saveItems });
      if (defaultValue === DP_VERSION) {
        setVersionId(selectVersionId);
      }
    }

    close();
  };

  const retrieveItemHrchies = () => {
    itemHrchyService
      .getAll({ searchFilters: { activeFlg: true, isAll: true } })
      .then(response => {
        const { results } = response.data;
        setItemHrchies(results);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createViewState("dpInitialSetting", itemHrchies);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      });
  };

  const retrieveCustHrchies = () => {
    customerHrchyService
      .getAll({ searchFilters: { activeFlg: true, isAll: true } })
      .then(response => {
        const { results } = response.data;
        setCustomerHrchies(results);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createViewState("dpInitialSetting", itemHrchies);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      });
  };

  const setttingInitialValues = configData => {
    if (!isEmpty(configData)) {
      if (configData?.objVal) {
        const objKeys = keys(configData.objVal);
        for (const objKey of objKeys) {
          const objVal = configData.objVal[objKey];
          if (isEqual(objKey, "defaultValue")) {
            setDefaultValue(objVal);
          } else if (isEqual(objKey, CUST_LEVEL_ID)) {
            const selectCustLevel = custLevelCds?.filter(lv => isEqual(lv.id, objVal));
            setDefaultPlanCustLevel(selectCustLevel?.[0] ?? customer);
          } else if (isEqual(objKey, ITEM_LEVEL_ID)) {
            const selectItemLevel = itemLevelCds?.filter(lv => isEqual(lv.id, objVal));
            setDefaultPlanItemLevel(selectItemLevel?.[0] ?? item);
          } else if (isEqual(objKey, CUSTOMIZATION)) {
            const savedList = [];
            for (const appliedObj of objVal) {
              const savedObj = {};
              const appliKeys = keys(appliedObj);

              for (const appliKey of appliKeys) {
                const appliedVal = appliedObj[appliKey];
                if (isEqual(appliKey, "initialPlan")) {
                  savedObj.initialValue = appliedVal;

                  if (includes(hasLevelPlans, appliedVal)) {
                    const custLevelId = appliedObj.planCustLvId;
                    const itemLevelId = appliedObj.planItemLvId;
                    savedObj.planCustLevel = custLevelCds?.filter(lv => isEqual(lv.id, custLevelId))?.[0] ?? customer;
                    savedObj.planItemLevel = itemLevelCds?.filter(lv => isEqual(lv.id, itemLevelId))?.[0] ?? item;
                  }
                } else if (isEqual(appliKey, "itemHrchyId")) {
                  const hrchy = itemHrchies?.filter(hrchy => isEqual(hrchy.id, appliedVal));
                  savedObj.itemHrchy = hrchy?.[0];
                  if (savedObj.itemHrchy) {
                    savedObj[ITEM_LEVEL_ID] = itemLevelCds?.filter(lv => isEqual(lv.id, savedObj.itemHrchy?.hrchyLvId))?.[0] ?? item;
                  }
                } else if (isEqual(appliKey, "custHrchyId")) {
                  const hrchy = customerHrchies?.filter(hrchy => isEqual(hrchy.id, appliedVal));
                  savedObj.custHrchy = hrchy?.[0];
                  if (savedObj.custHrchy) {
                    savedObj[CUST_LEVEL_ID] = custLevelCds?.filter(lv => isEqual(lv.id, savedObj.custHrchy?.hrchyLvId))?.[0] ?? customer;
                  }
                } else if (isEqual(appliKey, ITEM_LEVEL_ID)) {
                  savedObj[ITEM_LEVEL_ID] = itemLevelCds?.filter(lv => isEqual(lv.id, appliedVal))?.[0] ?? item;
                } else if (isEqual(appliKey, CUST_LEVEL_ID)) {
                  savedObj[CUST_LEVEL_ID] = custLevelCds?.filter(lv => isEqual(lv.id, appliedVal))?.[0] ?? customer;
                } else if (isEqual(appliKey, "formula")) {
                  savedObj.formula = appliedVal;
                }
              }
              savedList.push(savedObj);
            }
            setSaveList(savedList);
          }
        }
      }
    }
  };

  const getSelectedMeasureList = selectedInitialVal => {
    const computeMeasureList = configDatas?.objVal?.[selectedInitialVal];
    if (computeMeasureList) {
      return computeMeasureList;
    }

    return WorkbenchUtil.getDefaultComputeList(selectedInitialVal);
  };

  useEffect(() => {
    if (open) {
      if (isEmpty(itemHrchies)) {
        retrieveItemHrchies();
      }
      if (isEmpty(customerHrchies)) {
        retrieveCustHrchies();
      }
    } else {
      setSnackInfo({ open: false, severity: "", content: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!isEmpty(itemHrchies) && !isEmpty(customerHrchies) && !isEmpty(configDatas)) {
      setttingInitialValues(configDatas);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemHrchies, customerHrchies, configDatas]);

  useEffect(() => {
    if (!isEmpty(oriMeasureList) && defaultValue) {
      const sourceList = codeUtil.getSourceMeasureList(oriMeasureList, defaultValue, posActualFlg);
      if (sourceList) {
        setInitialMeasureList(sourceList);
      }
      setComputeMeasureList(getSelectedMeasureList(defaultValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, oriMeasureList, posActualFlg]);

  useEffect(() => {
    if (!isEmpty(versionList)) {
      let filterData = [];
      if (defaultValue === LAST_RELEASED_VERSION) {
        filterData = versionList?.filter(x => x.statusCd === "RELEASED");
      } else if (defaultValue === DP_VERSION) {
        filterData = versionList?.filter(x => x.statusCd === "SCENARIO");
      } else if (defaultValue === BASELINE_FORECAST) {
        filterData = versionList?.filter(x => x.statusCd === "SCENARIO" && x.parent === null);
      }
      if (!isEmpty(filterData)) {
        setActiveVersionList(filterData);
        setSelectVersionId(filterData[0].id);
      } else {
        if (defaultValue !== LAST_RELEASED_VERSION) {
          setActiveVersionList(versionList);
          setSelectVersionId(versionList[0].id);
        } else {
          setActiveVersionList([]);
          setSelectVersionId("");
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoReleasedVersion") });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionList]);

  return (
    /* handleApply 는 modalResultData 로 부모에 넘길 뿐 저장하지 않으므로 confirm 유지.
       예전엔 width "30%"(반응형)였는데 셸은 고정 폭이라 프리셋 lg 로 맞춘다. */
    <BaseConfigModal
      open={open}
      onClose={handleClose}
      title={t("initialPlanDetailSetting")}
      onSave={handleApply}
      saveLabel={t("confirm")}
      saveIcon={<DoneAllIcon />}
      size="lg">
        <Box sx={{ minHeight: 200, maxHeight: "60vh", overflow: "auto" }}>
          {!isEmpty(custLevelCds) && !isEmpty(itemLevelCds) && !isEmpty(customerHrchies) && !isEmpty(itemHrchies) && !isEmpty(oriMeasureList) ? (
            <Box sx={{ height: "calc(100% - 20px)", p: 1 }}>
              <FormGroup sx={{ width: "100%", minWidth: 100 }}>
                <FormControl sx={{ width: "100%" }}>
                  <InputLabel>{t("default")}</InputLabel>
                  <Box sx={{ display: "flex" }}>
                    <Select
                      sx={{ width: "70%", mr: "1%" }}
                      id="defaultValue"
                      name="defaultValue"
                      value={defaultValue}
                      label={t("default")}
                      size="small"
                      variant="outlined"
                      onChange={event => {
                        handleChangeSelect(event);
                      }}>
                      {filteredInitialVals?.map(initalValue => {
                        return (
                          <MenuItem
                            key={initalValue}
                            value={initalValue}>
                            {t(initalValue)}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <CalculationButton
                      sx={{ ml: 1, mt: 1 }}
                      label="computeExpressSetting"
                      color={!isEmpty(computeMeasureList) ? "primary" : "black"}
                      disabled={defaultValue === NOTINITIAL}
                      onClick={filterActionItem}
                    />
                  </Box>
                </FormControl>
              </FormGroup>
              <Box sx={{ mt: 1, display: defaultValue === DP_VERSION ? "flex" : "none" }}>
                <FormControl sx={{ width: "70%" }}>
                  <InputLabel>{t("version")}</InputLabel>
                  <Select
                    name="versionId"
                    label={t("version")}
                    value={selectVersionId}
                    size="small"
                    variant="outlined"
                    disabled={defaultValue !== DP_VERSION}
                    onChange={event => {
                      setSelectVersionId(event.target.value);
                    }}>
                    {activeVersionList?.map(version => {
                      return (
                        <MenuItem
                          value={version.id}
                          key={version.id}>
                          {version.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
              <FormControlLevels
                t={t}
                initialValue={defaultValue}
                custLevels={custLevelCds}
                itemLevels={itemLevelCds}
                hasLevelPlans={hasLevelPlans}
                ITEM_LEVEL={"ITEM_LEVEL"}
                CUST_LEVEL={"CUST_LEVEL"}
                handleChangeSelect={handleChangeSelect}
                custLevelNm={"defaultPlanCustLevel"}
                itemLevelNm={"defaultPlanItemLevel"}
                selectedCustLv={defaultPlanCustLevel}
                selectedItemLv={defaultPlanItemLevel}
                dpSettingCode={dpSettingCode}
              />
              <Divider sx={{ mt: 2, mb: 1, borderWidth: 1 }} />
              <BoxSelectInitialValues
                t={t}
                itemLevelCds={itemLevelCds}
                custLevelCds={custLevelCds}
                initialValues={initialValues}
                customerHrchies={customerHrchies}
                itemHrchies={itemHrchies}
                item={item}
                hasLevelPlans={hasLevelPlans}
                customer={customer}
                setDialogInfo={setDialogInfo}
                filteredInitialVals={filteredInitialVals}
                appliedList={saveList}
                setAppliedList={setSaveList}
                oriMeasureList={oriMeasureList}
                dpSettingCode={dpSettingCode}
                versionList={versionList}
                posActualFlg={posActualFlg}
              />
            </Box>
          ) : null}
        </Box>
        <Dialog
          open={dialogInfo.open}
          onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
          actionName={dialogInfo.actionName}
          title={dialogInfo.title}
          content={dialogInfo.content}
          onHandler={handleDialog}
          el={dialogInfo.el}
        />
        <Snackbar
          open={snackInfo.open}
          onClose={() => setSnackInfo({ ...snackInfo, open: false })}
          severity={snackInfo.severity}
          content={snackInfo.content}
        />
        <ComputeMeasureModal
          open={filterModalOpen}
          close={handelFilterClose}
          modalResultData={setComputeMeasureList}
          target={initialTarget?.length > 0 ? initialTarget : oriMeasureList}
          source={initialMeasureList?.length > 0 ? initialMeasureList : oriMeasureList}
          computeList={computeMeasureList}
        />
    </BaseConfigModal>
  );
};

export default withTranslation()(DpInitialSettingModal);
